import { css, cx } from "@emotion/css";
import { tableMobileStyles, tableStyles } from "@octopusdeploy/design-system-components";
import type { ReactNode } from "react";
import type React from "react";
import { useMediaQuery } from "react-responsive";
export interface DataTableColumn<TData> {
    title: string;
    render: (item: TData) => ReactNode;
    isChild?: (item: TData) => boolean;
    accessibleName?: string;
    width?: string;
    padding?: string;
    backgroundColor?: (item: TData) => string | undefined;
    ref?: React.RefObject<HTMLTableCellElement>;
    collapseNullCellsUpwards?: boolean;
    verticalAlign?: "top" | "middle";
    hideHeaderSeparator?: boolean;
}
export interface GroupedDataTableProps<TData> {
    columns: DataTableColumn<TData>[];
    data: TData[];
    getRowKey: (item: TData) => React.Key;
    accessibleName?: string;
    groupBy?: GroupBy<TData>[];
    tableClassName?: string;
}
export interface GroupBy<TData> {
    key: string;
    selector: (item: TData) => string;
    header: React.ReactElement;
}
export function GroupedDataTable<TData>({ columns, data, getRowKey, accessibleName, groupBy, tableClassName }: GroupedDataTableProps<TData>) {
    const isLargerThanIpad = useMediaQuery({ query: `(min-width: 811px)` });
    function groupData<TData>(data: TData[], groupBy: GroupBy<TData>[]) {
        const groupedData: Record<string, {
            items: TData[];
            header?: React.ReactElement;
        }> = {};
        groupBy.forEach((group) => {
            groupedData[group.key] = {
                items: [],
                header: group.header,
            };
        });
        data.forEach((item) => {
            const group = groupBy.find((g) => g.selector(item) === g.key);
            if (group === undefined) {
                return;
            }
            groupedData[group.key].items.push(item);
        });
        return groupedData;
    }
    function getRowSpan(column: DataTableColumn<TData>, currentItemIndex: number, items: TData[]): number | null {
        // For now, collapseNullCellsUpwards is only supported if there is no groupBy
        // With no groupBy, items correspond to rows
        if (!column.collapseNullCellsUpwards || groupBy !== undefined || items.length === 1) {
            return 1;
        }
        const currentItem = items[currentItemIndex];
        // Check there is a non-null cell above
        let nonNullCellAbove = false;
        if (currentItemIndex > 0) {
            for (let i = currentItemIndex - 1; i >= 0; i--) {
                const ithRowData = items[i];
                if (column.render(ithRowData) !== null) {
                    nonNullCellAbove = true;
                    break;
                }
            }
        }
        // If all cells above are null, use the default row span of 1 to prevent unexpected behavior
        if (currentItemIndex > 0 && !nonNullCellAbove) {
            return 1;
        }
        if (column.render(currentItem) === null) {
            return null;
        }
        let countNullCellsBelow = 0;
        for (let j = currentItemIndex + 1; j < items.length; j++) {
            const jthRowData = items[j];
            if (column.render(jthRowData) === null) {
                countNullCellsBelow++;
            }
            else {
                // If we encounter a non-null cell, stop counting
                break;
            }
        }
        return countNullCellsBelow + 1;
    }
    const groupedData = groupBy ? groupData(data, groupBy) : { "": { items: data } };
    const headings = columns.map((c, index) => (<th key={c.title || index} aria-label={c.accessibleName} style={{ width: c.width }} ref={c.ref} className={cx({ [hideHeaderSeparatorStyle]: c.hideHeaderSeparator })}>
            {c.title}
        </th>));
    const rows = Object.entries(groupedData).flatMap(([groupKey, { items, header }]) => [
        groupKey !== "" && items.length > 0 && header,
        ...items.map((itemData: TData, itemDataIndex: number) => {
            return (<tr key={getRowKey(itemData)}>
                    {columns.map((columnData, columnDataIndex) => {
                    const cellCssClass = columnData.isChild && columnData.isChild(itemData) ? childColumnStyles : undefined;
                    const cellStyle = {
                        width: columnData.width,
                        padding: columnData.padding,
                        backgroundColor: columnData.backgroundColor && columnData.backgroundColor(itemData),
                    };
                    const rowSpan: number | null = getRowSpan(columnData, itemDataIndex, items);
                    return rowSpan !== null ? (<td key={columnDataIndex} className={cx(cellCssClass, { [verticalAlignTopStyle]: columnData.verticalAlign === "top", [verticalAlignMiddleStyle]: columnData.verticalAlign === "middle" || !columnData.verticalAlign })} style={cellStyle} aria-label={columnData.accessibleName} rowSpan={rowSpan}>
                                {columnData.render(itemData)}
                            </td>) : null;
                })}
                </tr>);
        }),
    ]);
    return (<table className={cx(tableStyles, tableClassName, fixedStyle, { [tableMobileStyles]: !isLargerThanIpad })} aria-label={accessibleName}>
            <thead>
                <tr>{headings}</tr>
            </thead>
            <tbody>{rows}</tbody>
        </table>);
}
const childColumnStyles = css({
    borderTop: "0 !important",
});
const fixedStyle = css({
    tableLayout: "fixed",
});
const verticalAlignTopStyle = css({
    verticalAlign: "top !important",
});
const verticalAlignMiddleStyle = css({
    verticalAlign: "middle !important",
});
const hideHeaderSeparatorStyle = css({
    "&::before": {
        borderWidth: "0px !important",
    },
});
